import { AccountFacade } from '@mode/accounts/contract';
import { DatasourceFacade } from '@mode/accounts/domain';
import { ReportViewerFacade } from '@mode/report-viewer/domain';
import type { Ng1StateDeclaration, StateParams, StateProvider, UrlRouterProvider } from '@uirouter/angularjs';
import * as fromServices from '~/modules/report-viewer/services';
import { AnalyticsService, AnalyticsEvent } from '@mode/shared/util-segment';
import { explorationRoutes } from './exploration.routes';
import { FeatureFlag } from '@mode/shared/contract-common';
import { filter, first, map } from 'rxjs/operators';
import { isPresent, getPerformanceReport } from '@mode/shared/util-js';
import { ReportViewDataService } from '~/modules/report-viewer/services';
/**
 * NOTE: Alias is not a UI-Router standard.
 * It is a common in many routing frameworks and will redirect the other routes to the current state.
 * This is accomplished in ui-router with transitionTo which is registered below in this file:
 * Reference https://github.com/angular-ui/ui-router/issues/185
 *
 * It is in place to group all routes which resolve to a single component.
 */
interface Route extends Ng1StateDeclaration {
  alias?: string[];
}

export function configureReportRoutes($stateProvider: StateProvider, $urlRouterProvider: UrlRouterProvider) {
  const routes: Array<Route> = [...reportRoutes, ...explorationRoutes];

  routes.map((route) => $stateProvider.state(route));

  /**
   * NOTE: Alias is not a UI-Router standard.
   * It is a common convention in most other routing frameworks and will redirect the other routes to the current state.
   * This is accomplished in ui-router with transitionTo which is registered below in this file:
   * Reference https://github.com/angular-ui/ui-router/issues/185
   *
   * We are utilizing it here to bring all routes which resolve to a certain state into the common block of code.
   */

  routes.map((route) => {
    if (!route.alias) {
      return;
    }
    route.alias.map((aliasURL) => {
      $urlRouterProvider.when(aliasURL, [
        '$match',
        '$state',
        ($match, $state) => {
          $state.transitionTo(route.name, $match, {
            location: 'replace',
          });
        },
      ]);
    });
  });
}

const reportRoutes: Array<Route> = [
  {
    name: 'reportShow',
    url: '/:account/reports/:reportId',
    template: `<report-view-container page-context="$resolve.pageContext"></report-view-container>`,
    resolve: {
      pageContext: () => AnalyticsEvent.SubContext.ReportView,
    },
    onEnter: [
      fromServices.REPORT_VIEW_DATA_SERVICE_ID,
      'AnalyticsService',
      '$stateParams',
      'AccountFacade',
      (
        reportViewDataService: fromServices.ReportViewDataService,
        analyticsService: AnalyticsService,
        $stateParams: StateParams,
        accountFacade: AccountFacade
      ) => {
        /**
         * !! Important note on GF report view initialization !!
         *
         * `report.view.data.service.ts` has a helper called `initContainer` that is used to minimally initialize the app
         * container which does initial setup and Account init. This is being used for the GF exploration flow. Ideally it
         * will also be used for the report flow in the future, however, there are some legacy elements that need to (for
         * the time being) depend on the old flows and old state.
         *
         * For the time being, the GF flow will initialize things by using the `initReport` helper, doing the full legacy
         * initialization. Within the report view and view details, the following areas are the only places that are allowed
         * to depend on these legacy flows and legacy state (selectedReportState).
         * - platform header
         * - notebooks (report view and view details)
         * - schedules
         * - run history
         * - fullscreen polling in `ReportViewDataService`
         * - legacy visualization content
         *
         * Net new components or behavior CANNOT pull state off of the legacy services or `selectedReportState`, it must depend
         * on state reduced by the new APIs.
         */

        analyticsService.startTiming(AnalyticsEvent.Name.LoadingReport, {
          owner: $stateParams.account,
          report: $stateParams.reportId,
        });

        accountFacade.loadReportOwner($stateParams.account);
        reportViewDataService.initReport(); // When legacy flows are no longer necessary, we'll be able to `initContainer()` here
        // Note - If you want to test/validate that new behavior does not depend on legacy flows, you can
        // replace the above with `reportViewDataService.initContainer()`

        // Actual GF report flow initialization happens in the `reportShow.noRuns` and the `reportShow.runs` routes.
      },
    ],
    abstract: true,
  },
  {
    name: 'reportShow.noRuns',
    url: '',
    template: `<div ui-view class="report-ui-view-container"></div>`,
    onEnter: [
      '$stateParams',
      'ReportViewerFacade',
      ($stateParams, reportViewerFacade: ReportViewerFacade) => {
        // Kick off the report flow when no `runs/:run_id` param is present in the URL. Results in
        // most recent successful run.
        reportViewerFacade.selectReport($stateParams.account, $stateParams.reportId, null);
      },
    ],
    abstract: true,
  },
  window['MODE_FLAGS'][FeatureFlag.FastReportView] && getPerformanceReport(window.document)
    ? {
        name: 'reportShow.noRuns.dashboard',
        url: '',
        views: { '@': { template: '' } },
        alias: [
          '/:account/reports/:reportId',
          '/:account/reports/:reportId/runs',
          '/:account/reports/:reportId/schedules',
          '/:account/reports/:reportId/runs/:runId/runs',
          '/:account/reports/:reportId/runs/:runId/schedules',
        ],
      }
    : {
        name: 'reportShow.noRuns.dashboard',
        url: '',
        template: `<report-dashboard></report-dashboard>`,
        alias: [
          '/:account/reports/:reportId',
          '/:account/reports/:reportId/runs',
          '/:account/reports/:reportId/schedules',
          '/:account/reports/:reportId/runs/:runId/runs',
          '/:account/reports/:reportId/runs/:runId/schedules',
        ],
      },
  // container component for left bar nav and various details views
  {
    name: 'reportShow.noRuns.detailsView',
    url: '/details',
    abstract: true,
    template: `<report-details-presenter></report-details-presenter>`,
  },
  // Details page sub-routes
  {
    name: 'reportShow.noRuns.detailsView.details',
    url: '',
    onEnter: [
      'DatasourceFacade',
      (facade: DatasourceFacade) => {
        facade.loadReportDatasources();
      },
    ],
    template: `<report-details></report-details>`,
  },
  {
    name: 'reportShow.noRuns.detailsView.detailsNotebook',
    url: '/notebook',
    template: `<report-details-notebook></report-details-notebook>`,
  },
  {
    name: 'reportShow.noRuns.detailsView.detailsQuery',
    url: '/queries/:queryId',
    template: `<report-details-query></report-details-query>`,
    onEnter: [
      '$stateParams',
      'ReportViewerFacade',
      'ReportViewDataService',
      ($stateParams, reportViewerFacade: ReportViewerFacade, reportViewDataService: ReportViewDataService) =>
        reportViewDataService.lastSuccessfulRunToken$
          .pipe(
            filter(isPresent),
            first(),
            map((lastSuccessfulRunToken) =>
              reportViewerFacade.loadQueryRun(
                $stateParams.account,
                $stateParams.reportId,
                lastSuccessfulRunToken,
                $stateParams.queryId
              )
            )
          )
          .subscribe(),
    ],
  },
  {
    name: 'reportShow.noRuns.detailsView.detailsDataset',
    url: '/queries/:queryId/data',
    template: `<report-details-dataset></report-details-dataset>`,
    onEnter: [
      '$stateParams',
      'ReportViewerFacade',
      'ReportViewDataService',
      ($stateParams, reportViewerFacade: ReportViewerFacade, reportViewDataService: ReportViewDataService) =>
        reportViewDataService.lastSuccessfulRunToken$
          .pipe(
            filter(isPresent),
            first(),
            map((lastSuccessfulRunToken) =>
              reportViewerFacade.loadQueryRun(
                $stateParams.account,
                $stateParams.reportId,
                lastSuccessfulRunToken,
                $stateParams.queryId
              )
            )
          )
          .subscribe(),
    ],
  },
  {
    name: 'reportShow.noRuns.detailsView.presentation',
    url: '/html',
    template: `<report-presentation></report-presentation>`,
  },
  // end details page sub-routes
  {
    name: 'reportShow.noRuns.notebook',
    url: '/notebook/focus',
    template: `<report-notebook></report-notebook>`,
  },
  {
    name: 'reportShow.noRuns.queries',
    url: '/queries/:queryId',
    template: `<report-query-dataset-container></report-query-dataset-container>`,
    abstract: true,
  },
  {
    name: 'reportShow.noRuns.queries.details',
    url: '/focus',
    template: `<mode-query [query-id]="ctrl.queryToken$ | async:this" [is-raw-query]="ctrl.showParameterCode"><mode-query>`,
  },
  {
    name: 'reportShow.noRuns.queries.data',
    url: '/data/focus',
    template: `<report-query-result></report-query-result>`,
  },
  {
    name: 'reportShow.noRuns.viz',
    url: '/viz1/:vizId',
    template: `<report-legacy-visualizations-explore></report-legacy-visualizations-explore>`,
  },
  {
    name: 'reportShow.noRuns.chart',
    url: '/viz/:vizId',
    template: `<div class="report-ui-view-container" ui-view></div>`,
    abstract: true,
  },
  {
    name: 'reportShow.noRuns.chart.view',
    url: '/focus',
    template: `<viz-viewer></viz-viewer>`,
  },
  {
    name: 'reportShow.noRuns.chart.explore',
    url: '/explore',
    template: '<report-visualizations-explore></report-visualizations-explore>',
    data: {
      hasChanges: false,
    },
  },
  {
    name: 'reportShow.noRuns.pythonviz',
    url: '/pythonviz/:vizId',
    template: `<ui-view></ui-view>`,
    abstract: true,
  },
  {
    name: 'reportShow.noRuns.pythonviz.view',
    url: '',
    template: `<report-legacy-visualizations-explore></report-legacy-visualizations-explore>`,
  },
  {
    name: 'reportShow.noRuns.pythonviz.explore',
    url: '/explore',
    template: `<h1>Lets explore some python</h1>`,
    data: {
      hasChanges: false,
    },
  },

  /************************
   * URL state below is exactly the same except has :runId
   * These are permanent URLs that an end-user may bookmark if they view
   * an explicit run (or click the run button from a report view).
   ************************/
  {
    name: 'reportShow.runs',
    url: '/runs/:runId',
    template: `<div ui-view class="report-ui-view-container"></div>`,
    alias: ['/:account/reports/:reportId/runs/:runId'],
    onEnter: [
      '$stateParams',
      'ReportViewerFacade',
      ($stateParams, reportViewerFacade: ReportViewerFacade) => {
        // Kick off the report flow when `runs/:run_id` param is present in the URL. Results in
        // the report with that SPECIFIC run context.
        reportViewerFacade.selectReport($stateParams.account, $stateParams.reportId, $stateParams.runId);
      },
    ],
    abstract: true,
  },
  {
    name: 'reportShow.runs.dashboard',
    url: '',
    template: `<report-dashboard></report-dashboard>`,
  },
  {
    name: 'reportShow.runs.detailsView',
    url: '/details',
    template: `<report-details-presenter></report-details-presenter>`,
    abstract: true,
  },
  // Details page sub-routes for query runs
  {
    name: 'reportShow.runs.detailsView.details',
    url: '',
    onEnter: [
      'DatasourceFacade',
      (facade: DatasourceFacade) => {
        facade.loadReportDatasources();
      },
    ],
    template: `<report-details></report-details>`,
  },
  {
    name: 'reportShow.runs.detailsView.detailsNotebook',
    url: '/notebook',
    template: `<report-details-notebook></report-details-notebook>`,
  },
  {
    name: 'reportShow.runs.detailsView.detailsQuery',
    url: '/queries/:queryId',
    template: `<report-details-query></report-details-query>`,
    onEnter: [
      '$stateParams',
      'ReportViewerFacade',
      'DataViewFacade',
      ($stateParams, reportViewerFacade: ReportViewerFacade) =>
        reportViewerFacade.loadQueryRun(
          $stateParams.account,
          $stateParams.reportId,
          $stateParams.runId,
          $stateParams.queryId
        ),
    ],
  },
  {
    name: 'reportShow.runs.detailsView.detailsDataset',
    url: '/queries/:queryId/data',
    template: `<report-details-dataset></report-details-dataset>`,
    onEnter: [
      '$stateParams',
      'ReportViewerFacade',
      'DataViewFacade',
      ($stateParams, reportViewerFacade: ReportViewerFacade) =>
        reportViewerFacade.loadQueryRun(
          $stateParams.account,
          $stateParams.reportId,
          $stateParams.runId,
          $stateParams.queryId
        ),
    ],
  },
  {
    name: 'reportShow.runs.detailsView.presentation',
    url: '/html',
    template: `<report-presentation></report-presentation>`,
  },
  // End details page sub-routes for query runs
  {
    name: 'reportShow.runs.notebook',
    url: '/notebook/focus',
    template: `<report-notebook></report-notebook>`,
  },
  {
    name: 'reportShow.runs.queries',
    url: '/queries/:queryId',
    template: `<report-query-dataset-container></report-query-dataset-container>`,
    abstract: true,
  },
  {
    name: 'reportShow.runs.queries.details',
    url: '/focus',
    template: `<mode-query [is-raw-query]="ctrl.showParameterCode"><mode-query>`,
  },
  {
    name: 'reportShow.runs.queries.data',
    url: '/data/focus',
    template: `<report-query-result></report-query-result>`,
  },
  {
    name: 'reportShow.runs.viz',
    url: '/viz1/:vizId',
    template: `<report-legacy-visualizations-explore></report-legacy-visualizations-explore>`,
  },
  {
    name: 'reportShow.runs.chart',
    url: '/viz/:vizId',
    template: `<div class="report-ui-view-container" ui-view></div>`,
    abstract: true,
  },
  {
    name: 'reportShow.runs.chart.view',
    url: '/focus',
    template: `<viz-viewer></viz-viewer>`,
  },
  {
    name: 'reportShow.runs.chart.explore',
    url: '/explore',
    template: '<report-visualizations-explore></report-visualizations-explore>',
    data: {
      hasChanges: false,
    },
  },
  {
    name: 'reportShow.runs.pythonviz',
    url: '/pythonviz/:vizId',
    template: `<ui-view></ui-view>`,
    abstract: true,
  },
  {
    name: 'reportShow.runs.pythonviz.view',
    url: '',
    template: `<report-legacy-visualizations-explore></report-legacy-visualizations-explore>`,
  },
  {
    name: 'reportShow.runs.pythonviz.explore',
    url: '/explore',
    template: `<h1>Lets explore some python</h1>`,
    data: {
      hasChanges: false,
    },
  },
];
